import { EditOutlined } from "@ant-design/icons";
import { Badge, Button, Collapse, Descriptions } from "antd";
import moment from "moment";
import React from "react";
import { LoanRequestKyc } from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  formatSnakeCase,
  formatter,
} from "../helpers/utilities";
import { tabPaneStyle } from "../helpers/constants";

const { Panel } = Collapse;

interface Props {
  loanRequestKyc: LoanRequestKyc;
  loanRequestState: string;
  onEdit?: () => void;
}

export const LoanRequestKycView: React.FC<Props> = ({
  loanRequestKyc,
  loanRequestState,
  onEdit,
}) => {
  const canEdit =
    loanRequestState !== "approved" &&
    loanRequestState !== "sent_to_provider" &&
    loanRequestState !== "disbursed";

  return (
    <Collapse style={{ margin: "20px 0" }}>
      <Panel
        header={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>
              KYC Information <Badge count={1} style={tabPaneStyle} />
            </span>
            {canEdit && onEdit && (
              <Button
                className="base-button"
                size="middle"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                Edit
              </Button>
            )}
          </div>
        }
        key={"LoanRequestKYC"}
      >
        <Descriptions bordered>
          {/* Personal Information */}
          {loanRequestKyc.fullName && (
            <Descriptions.Item label={<b>Full Name</b>}>
              {loanRequestKyc.fullName}
            </Descriptions.Item>
          )}
          {loanRequestKyc.dateOfBirth && (
            <Descriptions.Item label={<b>Date of Birth</b>}>
              {moment(loanRequestKyc.dateOfBirth).format("DD/MM/YYYY")}
            </Descriptions.Item>
          )}
          {loanRequestKyc.numberOfDependents !== null &&
            loanRequestKyc.numberOfDependents !== undefined && (
              <Descriptions.Item label={<b>Number of Dependents</b>}>
                {loanRequestKyc.numberOfDependents}
              </Descriptions.Item>
            )}

          {/* Contact Information */}
          {loanRequestKyc.phoneNumber && (
            <Descriptions.Item label={<b>Phone Number</b>}>
              {loanRequestKyc.phoneNumber}
            </Descriptions.Item>
          )}
          {loanRequestKyc.email && (
            <Descriptions.Item label={<b>Email</b>}>
              {loanRequestKyc.email}
            </Descriptions.Item>
          )}
          {loanRequestKyc.residentialAddress && (
            <Descriptions.Item label={<b>Residential Address</b>} span={2}>
              {loanRequestKyc.residentialAddress}
            </Descriptions.Item>
          )}

          {/* Identification */}
          {loanRequestKyc.idNumber && (
            <Descriptions.Item label={<b>ID Number</b>}>
              {loanRequestKyc.idNumber}
            </Descriptions.Item>
          )}
          {loanRequestKyc.idExpiryDate && (
            <Descriptions.Item label={<b>ID Expiry Date</b>}>
              {moment(loanRequestKyc.idExpiryDate).format("DD/MM/YYYY")}
            </Descriptions.Item>
          )}
          {(loanRequestKyc.ghanaCardFrontUrl ||
            loanRequestKyc?.loanRequest?.customer?.ghanaCardIdUrl) && (
            <Descriptions.Item label={<b>Ghana Card (Front)</b>}>
              <a
                target="_blank"
                href={
                  loanRequestKyc.ghanaCardFrontUrl ||
                  loanRequestKyc?.loanRequest?.customer?.ghanaCardIdUrl
                }
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Image
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {(loanRequestKyc.ghanaCardBackUrl ||
            loanRequestKyc?.loanRequest?.customer?.ghanaCardIdBackUrl) && (
            <Descriptions.Item label={<b>Ghana Card (Back)</b>}>
              <a
                target="_blank"
                href={
                  loanRequestKyc.ghanaCardBackUrl ||
                  loanRequestKyc?.loanRequest?.customer?.ghanaCardIdBackUrl
                }
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Image
                </Button>
              </a>
            </Descriptions.Item>
          )}

          {/* Employment Information */}
          {loanRequestKyc.occupation && (
            <Descriptions.Item label={<b>Occupation</b>}>
              {loanRequestKyc.occupation}
            </Descriptions.Item>
          )}
          {loanRequestKyc.employerName && (
            <Descriptions.Item label={<b>Employer Name</b>}>
              {loanRequestKyc.employerName}
            </Descriptions.Item>
          )}
          {loanRequestKyc.monthlyIncome && (
            <Descriptions.Item label={<b>Monthly Income</b>}>
              {formatter.format(loanRequestKyc.monthlyIncome)}
            </Descriptions.Item>
          )}

          {/* Next of Kin */}
          {loanRequestKyc.nextOfKinName && (
            <Descriptions.Item label={<b>Next of Kin Name</b>}>
              {loanRequestKyc.nextOfKinName}
            </Descriptions.Item>
          )}
          {loanRequestKyc.nextOfKinRelationship && (
            <Descriptions.Item label={<b>Next of Kin Relationship</b>}>
              {capitalizeFirstLetter(loanRequestKyc.nextOfKinRelationship)}
            </Descriptions.Item>
          )}
          {loanRequestKyc.nextOfKinPhone && (
            <Descriptions.Item label={<b>Next of Kin Phone</b>}>
              {loanRequestKyc.nextOfKinPhone}
            </Descriptions.Item>
          )}
          {loanRequestKyc.nextOfKinAddress && (
            <Descriptions.Item label={<b>Next of Kin Address</b>} span={2}>
              {loanRequestKyc.nextOfKinAddress}
            </Descriptions.Item>
          )}

          {/* Collateral */}
          {loanRequestKyc.collateralType && (
            <Descriptions.Item label={<b>Collateral Type</b>}>
              {capitalizeFirstLetter(loanRequestKyc.collateralType)}
            </Descriptions.Item>
          )}
          {loanRequestKyc.collateralValue && (
            <Descriptions.Item label={<b>Collateral Value</b>}>
              {formatter.format(loanRequestKyc.collateralValue)}
            </Descriptions.Item>
          )}
          {loanRequestKyc.collateralDescription && (
            <Descriptions.Item label={<b>Collateral Description</b>} span={2}>
              {loanRequestKyc.collateralDescription}
            </Descriptions.Item>
          )}

          {/* Banking & Repayment */}
          {loanRequestKyc.bankName && (
            <Descriptions.Item label={<b>Bank Name</b>}>
              {loanRequestKyc.bankName}
            </Descriptions.Item>
          )}
          {loanRequestKyc.bankAccountNumber && (
            <Descriptions.Item label={<b>Account Number</b>}>
              {loanRequestKyc.bankAccountNumber}
            </Descriptions.Item>
          )}
          {loanRequestKyc.bankAccountName && (
            <Descriptions.Item label={<b>Account Name</b>}>
              {loanRequestKyc.bankAccountName}
            </Descriptions.Item>
          )}
          {loanRequestKyc.modeOfRepayment && (
            <Descriptions.Item label={<b>Mode of Repayment</b>}>
              {formatSnakeCase(
                capitalizeFirstLetter(loanRequestKyc.modeOfRepayment),
              )}
            </Descriptions.Item>
          )}

          {/* Document Uploads - Now from Loan Request */}
          {loanRequestKyc.loanRequest?.passportUrl && (
            <Descriptions.Item label={<b>Passport Picture</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.passportUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.applicationFormUrl && (
            <Descriptions.Item label={<b>Application Form</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.applicationFormUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.threeMonthsPayslipUrl && (
            <Descriptions.Item label={<b>3 Months Payslip</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.threeMonthsPayslipUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.standingOrderUrl && (
            <Descriptions.Item label={<b>Standing Order</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.standingOrderUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.threeMonthBankStatementUrl && (
            <Descriptions.Item label={<b>3 Month Bank Statement</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.threeMonthBankStatementUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.threeMomoStatementUrl && (
            <Descriptions.Item label={<b>3 Month MoMo Statement</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.threeMomoStatementUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest
            ?.oneYearConsistentTier2ContributionUrl && (
            <Descriptions.Item label={<b>1 Year Tier 2 Contribution</b>}>
              <a
                target="_blank"
                href={
                  loanRequestKyc.loanRequest
                    .oneYearConsistentTier2ContributionUrl
                }
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.clearanceFormUrl && (
            <Descriptions.Item label={<b>Clearance Form</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.clearanceFormUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.settlementQuotationUrl && (
            <Descriptions.Item label={<b>Settlement Quotation</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.settlementQuotationUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.sixMonthsBankStatementUrl && (
            <Descriptions.Item label={<b>6 Months Bank Statement</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.sixMonthsBankStatementUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.sixMonthsPayslipsUrl && (
            <Descriptions.Item label={<b>6 Months Payslips</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.sixMonthsPayslipsUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}
          {loanRequestKyc.loanRequest?.employmentOfferLetterUrl && (
            <Descriptions.Item label={<b>Employment Offer Letter</b>}>
              <a
                target="_blank"
                href={loanRequestKyc.loanRequest.employmentOfferLetterUrl}
                rel="noopener noreferrer"
              >
                <Button type="link" size="small">
                  View Document
                </Button>
              </a>
            </Descriptions.Item>
          )}

          {/* Additional Notes */}
          {loanRequestKyc.notes && (
            <Descriptions.Item label={<b>Additional Notes</b>} span={3}>
              {loanRequestKyc.notes}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Panel>
    </Collapse>
  );
};
